import React, { useCallback, useMemo } from 'react';

import {
  getPhaseBuildBusyLabel,
  getPhaseBuildTitle,
  isPhaseBuildActionEnabled,
  usePhaseBuildState,
} from '../../hooks/usePhaseBuildState';
import type { PhaseBuildExecutionState } from '../../services/buildPhaseService';
import { getPhaseActionCreditShortfall } from '../../utils/creditGate';

import { usePhaseBuildSelectionPresentation } from './phaseTaskSelectionStore';
import type { PressFix } from './pressRefusal';
import { PressRefusalNote } from './PressRefusalNote';
import { ensureTaskSelectionPrompt } from './taskSelectionPrompt';
import { recordUserActionClick } from './useActionBarClicks';
import { type BuildPress, type BuildPressHandlers, useBuildPress } from './useBuildPress';
import { usePhaseTaskSelectionSummaryVersion } from './usePhaseTaskSelectionSummary';
import type { PressPhase } from './usePressFeedback';

type Selection = ReturnType<typeof usePhaseBuildSelectionPresentation>;
type PhaseBuild = ReturnType<typeof usePhaseBuildState>;

export interface BuildButtonGate {
  /** The phase picker is open with nothing selected: the press explains instead of building. */
  selectionEmpty: boolean;
  disabled: boolean;
  creditShortfall: number;
}

/** Whether a build button can be pressed, and what an empty selection or a shortfall means for the press. */
export const buildButtonGate = (state: PhaseBuildExecutionState | null, selection: Selection): BuildButtonGate => {
  const creditShortfall = getPhaseActionCreditShortfall(state, selection.creditShortfall, selection.active);
  const selectionEmpty = selection.active && (selection.selectedTaskIds?.length ?? 0) === 0
    && Boolean(state?.current_phase) && !state?.all_completed && state?.blocking_reason !== 'build_in_progress';
  const blockedOtherwise = selection.disabled && creditShortfall <= 0 && !selectionEmpty;
  const pressable = isPhaseBuildActionEnabled(state) || creditShortfall > 0 || selectionEmpty;

  return { selectionEmpty, creditShortfall, disabled: blockedOtherwise || !pressable };
};

/** Queued reads "Starting Phase N…", running "Building Phase N…"; idle and refused read the action. */
export const buildPressLabel = (phase: PressPhase, state: PhaseBuildExecutionState | null, idle: string): string => {
  const number = state?.current_phase?.phase_number;

  if (phase === 'queued') return getPhaseBuildBusyLabel(state);
  if (phase === 'running') return number ? `Building Phase ${number}…` : 'Building…';

  return idle;
};

export interface BuildButtonModel {
  phaseBuild: PhaseBuild;
  selection: Selection;
  gate: BuildButtonGate;
  press: BuildPress;
  label: string;
  title: string;
  onFix: (fix: PressFix) => void;
}

interface BuildButtonOptions {
  projectId?: string | null;
  sessionId?: string | null;
  entryId?: string;
  phase: string;
  buttonKey: string;
  onStarted?: () => void;
}

const useBuildHandlers = (
  { projectId, sessionId, entryId, phase, buttonKey, onStarted }: BuildButtonOptions,
  selection: Selection,
  refresh: PhaseBuild['refresh'],
): BuildPressHandlers => {
  const record = useCallback(() => {
    void recordUserActionClick(entryId, phase, buttonKey, selection.label, projectId, sessionId);
  }, [buttonKey, entryId, phase, projectId, selection.label, sessionId]);
  const restoreSelector = useCallback(() => {
    if (!projectId || !selection.phaseId) return;
    void ensureTaskSelectionPrompt(projectId, selection.phaseId, sessionId).catch(() => {});
  }, [projectId, selection.phaseId, sessionId]);
  const reread = useCallback(() => refresh(true), [refresh]);

  return useMemo(
    () => ({ record, restoreSelector, onStarted, refresh: reread }),
    [onStarted, record, reread, restoreSelector],
  );
};

/**
 * The fixes a build refusal carries out itself: press again, or open the phase tasks that need
 * attention. A listed step the note started (its gate button) clears the list: that step now runs.
 */
const useBuildFix = (press: BuildPress, phaseBuild: PhaseBuild) => useCallback((fix: PressFix) => {
  if (fix.kind === 'retry') press.press();
  if (fix.kind === 'resolve_tasks') void phaseBuild.resolveCurrentPhase().catch(() => {});
  if (fix.kind === 'prerequisite') press.feedback.reset();
}, [phaseBuild, press]);

/** Phase state, task selection, gate and press for one build button (Start Building, Build Tasks). */
export const useBuildButton = (options: BuildButtonOptions): BuildButtonModel => {
  const phaseBuild = usePhaseBuildState(options.projectId);
  const selection = usePhaseBuildSelectionPresentation(options.projectId, phaseBuild.state, phaseBuild.isLoading);
  const selectionSummaryId = usePhaseTaskSelectionSummaryVersion(
    options.projectId, selection.phaseId, selection.selectedTaskIds,
  );
  const gate = buildButtonGate(phaseBuild.state, selection);
  const press = useBuildPress({
    projectId: options.projectId,
    state: phaseBuild.state, selectionEmpty: gate.selectionEmpty, creditShortfall: gate.creditShortfall,
    phaseId: selection.phaseId, selectedTaskIds: selection.selectedTaskIds, selectionSummaryId,
  }, useBuildHandlers(options, selection, phaseBuild.refresh));
  const onFix = useBuildFix(press, phaseBuild);

  return {
    phaseBuild, selection, gate, press, onFix,
    label: buildPressLabel(press.feedback.phase, phaseBuild.state, selection.label),
    title: selection.disabledReason ?? getPhaseBuildTitle(phaseBuild.state),
  };
};

/** The build button and, when its press started nothing, the reason and fix beside it in the bar. */
export const BuildPressControl: React.FC<{
  build: BuildButtonModel;
  className: string;
  projectId?: string | null;
  disabled?: boolean;
}> = ({ build, className, projectId, disabled = false }) => (
  <>
    <button
      className={className}
      disabled={disabled || build.press.busy || build.phaseBuild.isLoading || build.gate.disabled}
      title={build.title}
      type="button"
      aria-busy={build.press.busy || undefined}
      onClick={build.press.press}
    >
      {build.press.busy && <span className="ai-overview-action-spinner" aria-hidden="true" />}
      <span className="ai-overview-action-btn__label">{build.label}</span>
    </button>
    <PressRefusalNote refusal={build.press.feedback.refusal} projectId={projectId} onFix={build.onFix} />
  </>
);
